/**
 * Shared helper for fetching + decrypting opaque encrypted binaries
 * from the relay. Both photos (`/photos/:id`) and voice notes
 * (`/photos/audio_:id` — same endpoint, the relay doesn't
 * distinguish) flow through here so the AES-CBC decode logic and
 * the 404 retry policy live in one place.
 *
 * Wire format (mobile/src/crypto/encryption.ts#encryptFile):
 *   1. mobile reads the file as base64
 *   2. AES-CBC encrypts that base64 string
 *   3. wraps as `ivHex:base64(ciphertext)` and POSTs the body
 *
 * On the web side we do the inverse + a final base64 decode to
 * recover the raw file bytes, then wrap in a Blob and mint an
 * object URL for the existing `<img>` / `<audio>` render paths.
 */
import { aesCbcDecrypt, hexToBytes } from '../crypto/webCrypto';
import { RELAY_HTTP_URL } from '../config/env';

/**
 * Fetch the relay-stored encrypted binary at `/photos/:relayId`,
 * AES-CBC-decrypt with the team key, base64-decode the inner
 * payload, wrap as a Blob with the supplied mime type, and return
 * a `URL.createObjectURL(...)` ready to use as an `<img src>` or
 * `<audio src>`.
 *
 * Single retry on 404 with a 2-second back-off — mobile uploads
 * the binary asynchronously after the WS metadata broadcast, and
 * the two can race on the receiver side. Anything beyond a single
 * retry would freeze the live-sync handler too long; the periodic
 * `pollImmediate` plus the mobile re-upload guarantees eventual
 * delivery if the binary still hasn't landed.
 *
 * Returns null on any failure (network, 404 after retry, malformed
 * wire, decrypt mismatch). Callers treat null as "not yet
 * available" and may retry on the next render.
 */
export async function downloadEncryptedBlobUrl(
  relayId: string,
  teamKeyHex: string,
  companyToken: string,
  mimeType: string,
): Promise<string | null> {
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const res = await fetch(`${RELAY_HTTP_URL}/photos/${relayId}`, {
        headers: { 'x-company-token': companyToken },
      });
      if (res.status === 404 && attempt === 0) {
        await new Promise((r) => setTimeout(r, 2000));
        continue;
      }
      if (!res.ok) return null;
      const wire = await res.text();
      const colon = wire.indexOf(':');
      if (colon === -1) return null;
      const ivBytes = hexToBytes(wire.slice(0, colon));
      const ctB64 = wire.slice(colon + 1);
      const ctBin = atob(ctB64);
      const ctBytes = new Uint8Array(ctBin.length);
      for (let i = 0; i < ctBin.length; i++) ctBytes[i] = ctBin.charCodeAt(i);
      const teamKey = hexToBytes(teamKeyHex);
      const plaintextBytes = await aesCbcDecrypt(teamKey, ctBytes, ivBytes);
      // The plaintext is the file's base64 representation (mobile
      // wraps with `encryptFile` which reads the file as base64
      // before encrypting). Decode once more to get the raw bytes.
      const fileB64 = new TextDecoder().decode(plaintextBytes);
      const fileBin = atob(fileB64);
      const fileBytes = new Uint8Array(fileBin.length);
      for (let i = 0; i < fileBin.length; i++) fileBytes[i] = fileBin.charCodeAt(i);
      const blob = new Blob([fileBytes], { type: mimeType });
      return URL.createObjectURL(blob);
    } catch {
      // Transient — fall through to retry or null below.
    }
  }
  return null;
}
